import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, Loader2, CheckCircle2, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStart = () => new Date().toISOString().slice(0, 8) + "01";

export default function PayrollPanel() {
  const qc = useQueryClient();
  const [loc, setLoc] = useState("");
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayISO());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: locations = [] } = useQuery({ queryKey: ["mine-locations"], queryFn: () => api.get("/admin/locations/mine").then((r) => r.data) });
  const activeLoc = loc || locations[0]?.id || "";
  const { data: runs = [] } = useQuery({
    queryKey: ["payroll-runs", activeLoc],
    queryFn: () => api.get(`/admin/payroll/runs?location_id=${activeLoc}`).then((r) => r.data),
    enabled: !!activeLoc,
  });

  const runPreview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/payroll/preview", { location_id: activeLoc, period_start: start, period_end: end }); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveRun = async () => {
    setBusy(true);
    try { await api.post("/admin/payroll/runs", { location_id: activeLoc, period_start: start, period_end: end }); toast.success("Payroll run saved"); setPreview(null); qc.invalidateQueries({ queryKey: ["payroll-runs"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const markPaid = async (id) => { await api.post(`/admin/payroll/runs/${id}/mark-paid`); toast.success("Marked paid"); qc.invalidateQueries({ queryKey: ["payroll-runs"] }); };
  const deleteRun = async (id) => {
    if (!window.confirm("Remove this payroll run? This cannot be undone.")) return;
    try { await api.delete(`/admin/payroll/runs/${id}`); toast.success("Payroll run removed"); qc.invalidateQueries({ queryKey: ["payroll-runs"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-payroll-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2 mb-1"><Banknote size={22} className="text-[#144F75]" /> Payroll</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Totals hours × rate per enhancer for a location & date range. Enhancer rate wins, else the location default.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={activeLoc} onChange={(e) => { setLoc(e.target.value); setPreview(null); }} data-testid="payroll-location" className="eyw-input !py-2 text-sm">
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name} ({money(l.hourly_rate)}/hr)</option>)}
        </select>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="eyw-input !py-2 text-sm" />
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="eyw-input !py-2 text-sm" />
        <button onClick={runPreview} disabled={busy || !activeLoc} data-testid="payroll-preview-btn" className="eyw-btn !py-2 text-sm disabled:opacity-60">{busy && <Loader2 className="animate-spin" size={14} />} Preview</button>
      </div>

      {preview && (
        <div className="eyw-card p-5 mb-6" data-testid="payroll-preview">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg text-[#1A242B]">Preview · {preview.period_start} → {preview.period_end}</h3>
            <button onClick={saveRun} disabled={busy || preview.lines.length === 0} data-testid="payroll-save-btn" className="eyw-btn !py-1.5 text-xs disabled:opacity-60">Save run</button>
          </div>
          <PayTable lines={preview.lines} total={preview.total} />
        </div>
      )}

      <h3 className="font-serif text-lg text-[#1A242B] mb-3 flex items-center gap-2"><FileText size={17} /> Saved runs</h3>
      <div className="space-y-3">
        {runs.length === 0 && <p className="text-sm text-[#4A5D6B]">No payroll runs yet.</p>}
        {runs.map((r) => (
          <div key={r.id} data-testid={`payroll-run-${r.id}`} className="eyw-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-[#1A242B]">{r.period_start} → {r.period_end}</p>
                <p className="text-xs text-[#8FA89B]">{r.lines.length} staff · {money(r.total)}</p>
              </div>
              <div className="flex items-center">
              {r.status === "paid" ? <span className="text-xs px-2.5 py-1 rounded-full bg-[#5C8A72]/15 text-[#5C8A72] flex items-center gap-1"><CheckCircle2 size={12} /> Paid</span>
                : <button onClick={() => markPaid(r.id)} data-testid={`mark-paid-${r.id}`} className="eyw-btn !py-1.5 text-xs">Mark paid</button>}
              <button onClick={() => deleteRun(r.id)} data-testid={`delete-run-${r.id}`} title="Remove run" className="ml-2 text-[#B5794E] hover:text-red-600 p-1"><Trash2 size={15} /></button>
              </div>
            </div>
            <PayTable lines={r.lines} total={r.total} compact />
          </div>
        ))}
      </div>
    </div>
  );
}

function PayTable({ lines, total, compact }) {
  return (
    <table className="w-full text-sm">
      <thead><tr className="text-left text-[#8FA89B] text-xs"><th className="pb-1">Enhancer</th><th>Hours</th><th>Rate</th><th className="text-right">Amount</th></tr></thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.enhancer_id} className="border-t border-[#E5E0D8]">
            <td className="py-1.5 text-[#1A242B]">{l.enhancer_name}</td>
            <td>{l.hours}</td><td>{money(l.rate)}</td><td className="text-right font-medium">{money(l.amount)}</td>
          </tr>
        ))}
        <tr className="border-t-2 border-[#144F75]/20"><td className="py-1.5 font-semibold text-[#1A242B]" colSpan={3}>Total</td><td className="text-right font-serif text-[#144F75]">{money(total)}</td></tr>
      </tbody>
    </table>
  );
}
